import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createCn } from '../../../helpers/utils';
import { ConversationInteractionMatrix, ConversationInterruptions } from '../../../services/api.service';
import { SpeakerLabelIndex, orderLabels, resolveLabelMeta } from '../utils/voice-analytics.utils';
import { AnalyticsCard } from './meeting-analytics-panel.component';
import classes from '../styles.module.scss';

const cn = createCn('file', classes);

type Props = {
  interactionMatrix: ConversationInteractionMatrix | null;
  interruptions: ConversationInterruptions | null;
  labelIndex: SpeakerLabelIndex;
};

const HEATMAP_BASE = '28, 155, 215'; // KTZH_PRIMARY, см. utils/meeting-analytics.ts

/** Задача B1: карточка «Кто кому отвечает» — heatmap interaction_matrix +
 *  направленные перебивания (interruptions.by_pair, "A→B: N"). */
const InteractionMatrixCard: React.FC<Props> = ({ interactionMatrix, interruptions, labelIndex }) => {
  const { t } = useTranslation();

  const speakers = useMemo(() => {
    if (!interactionMatrix?.edges.length) return [];
    const labels = new Set<string>();
    interactionMatrix.edges.forEach(e => { labels.add(e.from); labels.add(e.to); });
    return orderLabels(labels, labelIndex);
  }, [interactionMatrix, labelIndex]);

  const { countMap, maxCount } = useMemo(() => {
    const map: Record<string, Record<string, number>> = {};
    let max = 1;
    (interactionMatrix?.edges || []).forEach(edge => {
      if (!map[edge.from]) map[edge.from] = {};
      map[edge.from][edge.to] = edge.count;
      if (edge.count > max) max = edge.count;
    });
    return { countMap: map, maxCount: max };
  }, [interactionMatrix]);

  const interruptionPairs = useMemo(() => {
    if (!interruptions?.available || !interruptions.by_pair) return [];
    return Object.entries(interruptions.by_pair)
      .map(([key, count], i) => {
        const [from, to] = key.split('→');
        return {
          key,
          count,
          fromMeta: resolveLabelMeta(from, labelIndex, i),
          toMeta: resolveLabelMeta(to, labelIndex, i + 1),
        };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [interruptions, labelIndex]);

  const maxInterruptionCount = interruptionPairs[0]?.count || 1;

  if (!speakers.length) return null;

  return (
    <AnalyticsCard title={t('voice-analytics.interaction-title')} badge="data">
      <p className={cn('analytics-hint')}>{t('voice-analytics.interaction-hint')}</p>
      <div className={cn('voice-heatmap')}>
        <div
          className={cn('voice-heatmap-grid')}
          style={{ gridTemplateColumns: `5.5rem repeat(${speakers.length}, minmax(2.1rem, 1fr))` }}
        >
          <div className={cn('voice-heatmap-corner')} />
          {speakers.map((to, i) => {
            const toMeta = resolveLabelMeta(to, labelIndex, i);
            return (
              <div key={`col-${to}`} className={cn('voice-heatmap-col-head')} title={toMeta.displayName}>
                <span style={{ color: toMeta.color }}>{toMeta.shortName}</span>
              </div>
            );
          })}
          {speakers.map((from, rowIdx) => {
            const fromMeta = resolveLabelMeta(from, labelIndex, rowIdx);
            return (
              <React.Fragment key={`row-${from}`}>
                <div className={cn('voice-heatmap-row-head')} title={fromMeta.displayName}>
                  <span
                    className={cn('voice-heatmap-row-dot')}
                    style={{ backgroundColor: fromMeta.color }}
                  />
                  <span style={{ color: fromMeta.color }}>{fromMeta.shortName}</span>
                </div>
                {speakers.map(to => {
                  const count = countMap[from]?.[to] || 0;
                  const alpha = from === to ? 0 : Math.min(0.85, 0.12 + (count / maxCount) * 0.68);
                  return (
                    <div
                      key={`cell-${from}-${to}`}
                      className={cn('voice-heatmap-cell', { self: from === to })}
                      style={count > 0 ? { backgroundColor: `rgba(${HEATMAP_BASE}, ${alpha})` } : undefined}
                      title={from === to ? undefined : `${fromMeta.displayName} → ${resolveLabelMeta(to, labelIndex).displayName}: ${count}`}
                    >
                      {from === to ? '—' : (count > 0 ? count : '')}
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {interruptionPairs.length > 0 && (
        <>
          <p className={cn('analytics-subtitle')}>{t('voice-analytics.interaction-interruptions-title')}</p>
          <div className={cn('interruptions-list')}>
            {interruptionPairs.map(pair => (
              <div key={pair.key} className={cn('interruptions-row')}>
                <span className={cn('interruptions-pair')}>
                  <span style={{ color: pair.fromMeta.color }} title={pair.fromMeta.displayName}>
                    {pair.fromMeta.shortName}
                  </span>
                  <span className={cn('interruptions-arrow')}>→</span>
                  <span style={{ color: pair.toMeta.color }} title={pair.toMeta.displayName}>
                    {pair.toMeta.shortName}
                  </span>
                </span>
                <div className={cn('interruptions-bar-track')}>
                  <div
                    className={cn('interruptions-bar-fill')}
                    style={{
                      width: `${(pair.count / maxInterruptionCount) * 100}%`,
                      background: `linear-gradient(90deg, ${pair.fromMeta.color}, ${pair.toMeta.color})`,
                    }}
                  />
                </div>
                <span className={cn('interruptions-count')}>{pair.count}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </AnalyticsCard>
  );
};

export default InteractionMatrixCard;
